/**
 * SignaturePad — minimal HTML5 canvas drawing for "drawn" signatures.
 *
 * - Touch + mouse + pointer events
 * - Smooth lines via quadraticCurveTo
 * - Clear button
 * - Returns the signature as a data-URL PNG via `onChange(dataUrl)`
 *
 * Why not the npm `signature_pad` library?
 *   - Adds ~30kb for a single feature
 *   - We only need a clean draw → PNG export. This file is <100 lines.
 */
import React, { useRef, useEffect, useState, useCallback } from "react";
import { RotateCcw } from "lucide-react";

export default function SignaturePad({ onChange, height = 140, disabled = false }) {
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  // Make canvas crisp on retina
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 1.8;
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - r.left, y: e.touches[0].clientY - r.top };
    }
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
  }, [disabled]);

  const move = useCallback((e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.quadraticCurveTo(
      (lastRef.current.x + p.x) / 2,
      (lastRef.current.y + p.y) / 2,
      p.x, p.y,
    );
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  }, [disabled, hasInk]);

  const end = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    // Emit
    if (onChange && hasInk) onChange(canvasRef.current.toDataURL("image/png"));
  }, [onChange, hasInk]);

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    if (onChange) onChange("");
  };

  return (
    <div className="relative" data-testid="signature-pad">
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: `${height}px`, touchAction: "none" }}
        className={`block rounded-lg border-2 border-dashed bg-white transition-colors ${
          hasInk ? "border-[#0055FF]/60" : "border-slate-300 hover:border-slate-400"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-crosshair"}`}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {!hasInk && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-[12px] text-slate-400 font-mono">
          Draw your signature here ↑
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="text-slate-500">By drawing your signature you confirm you have authority to bind your company to this agreement.</span>
        {hasInk && (
          <button onClick={clear} type="button" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900" data-testid="signature-pad-clear">
            <RotateCcw className="size-3" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}
